import { Pencil, ChevronLeft, ChevronRight, Database } from "lucide-react";
import {
  fontSize,
  fontWeight,
  textColor,
  bgColor,
  borderColor,
  focusStyle,
  statusBadge,
  toggleColors,
} from "@/theme";
import Pagination from "../common/Pagination";

export interface Column<T> {
  key: keyof T;
  label: string;
  render?: (value: any, item: T) => React.ReactNode;
}
interface MasterTableProps<T = any> {
  columns: Column<T>[];
  data: T[];
  onEdit?: (item: T) => void;
  onToggleActive?: (item: T) => void;
  isLoading?: boolean;
  togglingId?: string | number | null;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  canEdit?: boolean;
  canToggle?: boolean;
  totalCount?: number;
}

export default function MasterTable<T>({
  columns,
  data,
  onEdit,
  onToggleActive,
  isLoading = false,
  togglingId = null,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  canEdit = true,
  canToggle = false,
  totalCount,
}: MasterTableProps<T>) {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500 mx-auto mb-4"></div>
          <p className={`${textColor.muted} ${fontWeight.medium}`}>Loading data...</p>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="text-center py-16">
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${bgColor.primaryLight} mb-4`}>
          <Database className={`w-8 h-8 ${textColor.primary}`} />
        </div>
        <p className={`${textColor.muted} ${fontWeight.medium} ${fontSize.lg}`}>No data found</p>
      </div>
    );
  }

  const hasIsActiveField =
    data.length > 0
      ? Object.prototype.hasOwnProperty.call(data[0], "isActive") ||
      Object.prototype.hasOwnProperty.call(data[0], "Is_Active")
      : false;

  return (
    <div className="space-y-4">
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        itemsPerPage={20}
        onPageChange={(page) => {
          onPageChange?.(page);
        }}
      />
      <div className={`overflow-x-auto rounded-lg border ${borderColor.gray200}`}>
        <table className="w-full">
          <thead>
            <tr className={`bg-gradient-to-r from-gray-50 to-gray-100 border-b ${borderColor.gray200}`}>
              {columns.map((column) => (
                <th
                  key={String(column.key)}
                  className={`px-6 py-4 text-left ${fontSize.sm} ${fontWeight.semibold} ${textColor.secondary}`}
                >
                  {column.label}
                </th>
              ))}
              {hasIsActiveField && (
                <th className={`px-6 py-4 text-left ${fontSize.sm} ${fontWeight.semibold} ${textColor.secondary}`}>
                  Status
                </th>
              )}
              <th className={`px-6 py-4 text-left ${fontSize.sm} ${fontWeight.semibold} ${textColor.secondary}`}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => {
              const isActive = (item as any).isActive || (item as any).Is_Active;
              const itemId = (item as any).id;
              const hasIsActive = Object.prototype.hasOwnProperty.call(item, "isActive") ||
                Object.prototype.hasOwnProperty.call(item, "Is_Active");
              return (
                <tr
                  key={index}
                  className={`border-b ${borderColor.gray200} hover:bg-brand-50 transition-colors`}
                >
                  {columns.map((column) => (
                    <td key={String(column.key)} className="py-4 px-4 text-gray-700 text-base">
                      {column.render
                        ? column.render(item, item)
                        : String(item[column.key] ?? "N/A")
                      }
                    </td>
                  ))}
                  {hasIsActive && (
                    <td className={`px-6 py-4 ${fontSize.sm}`}>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full ${fontSize.xs} ${fontWeight.medium} ${isActive ? statusBadge.active : statusBadge.inactive
                          }`}
                      >
                        {isActive ? "Active" : "Inactive"}
                      </span>
                    </td>)}
                  <td className={`px-6 py-4 ${fontSize.sm}`}>
                    <div className="flex gap-2 items-center">
                      {canEdit && onEdit && (
                        <button
                          onClick={() => onEdit?.(item)}
                          className={`inline-flex items-center justify-center p-2 ${textColor.primary} hover:${bgColor.primaryMedium} rounded-lg transition-colors`}
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      {canToggle && (
                        <button
                          onClick={() => onToggleActive(item)}
                          disabled={togglingId === itemId}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${togglingId === itemId ? "cursor-not-allowed" : "cursor-pointer"
                            }`}
                          style={{
                            backgroundColor: isActive ? "#22c55e" : "#ef4444", // green-500 / red-500
                          }}
                          title={isActive ? "Deactivate" : "Activate"}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isActive ? "translate-x-6" : "translate-x-1"
                              }`}
                          />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
